import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, HelpCircle, StickyNote } from 'lucide-react';
import { listContext } from '@/services/knowledgeApi';
import type {
  ContextItem,
  TenantContextItem,
  UserContextItem,
} from '@Types/knowledge';
import { KnowledgeAddMenu } from './KnowledgeAddMenu';
import { KnowledgeMemoryPanel } from './KnowledgeMemoryPanel';
import {
  KnowledgeScopePills,
  type KnowledgeScopeFilter,
} from './KnowledgeScopePills';
import { KnowledgeSourceTile } from './KnowledgeSourceTile';

interface KnowledgeLibraryTabProps {
  refreshKey?: number;
  onOpenNote: (
    contextId: string,
    scope: 'user' | 'tenant',
    readOnly?: boolean,
  ) => void;
  onOpenSource: (
    contextId: string,
    scope: 'user' | 'tenant',
    readOnly?: boolean,
  ) => void;
  onCreateNew: (
    scope: 'user' | 'tenant',
    options?: { defaultApplyMode?: 'always' | 'chat' },
  ) => void;
  onUploadSource: (scope: 'user' | 'tenant') => void;
  onLearnMore?: () => void;
}

const isDocument = (item: ContextItem) => item.item_type === 'document';
const isAlwaysOn = (item: ContextItem) => item.apply_mode === 'always';

const matchesScope = (item: ContextItem, filter: KnowledgeScopeFilter) => {
  if (filter === 'all') return true;
  if (filter === 'team') return item.scope === 'tenant';
  return item.scope === 'user';
};

export const KnowledgeLibraryTab = ({
  refreshKey,
  onOpenNote,
  onOpenSource,
  onCreateNew,
  onUploadSource,
  onLearnMore,
}: KnowledgeLibraryTabProps) => {
  const [userItems, setUserItems] = useState<UserContextItem[]>([]);
  const [tenantItems, setTenantItems] = useState<TenantContextItem[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [scopeFilter, setScopeFilter] = useState<KnowledgeScopeFilter>('all');

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await listContext();
      setUserItems(res.user);
      setTenantItems(res.tenant);
      setIsAdmin(!!res.is_admin);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load library');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh, refreshKey]);

  const allItems = useMemo(
    () => [...userItems, ...tenantItems],
    [userItems, tenantItems],
  );

  const hasProcessingDocuments = useMemo(
    () =>
      allItems.some(
        item =>
          item.item_type === 'document' &&
          (item.ingestion_status ?? 'ready') === 'processing',
      ),
    [allItems],
  );

  useEffect(() => {
    if (!hasProcessingDocuments) return undefined;
    const timer = window.setInterval(() => {
      refresh();
    }, 4000);
    return () => window.clearInterval(timer);
  }, [hasProcessingDocuments, refresh]);

  const memoryItems = useMemo(
    () =>
      allItems.filter(
        item =>
          !isDocument(item) &&
          isAlwaysOn(item) &&
          matchesScope(item, scopeFilter),
      ),
    [allItems, scopeFilter],
  );

  const sourceItems = useMemo(
    () =>
      allItems.filter(item => {
        if (!matchesScope(item, scopeFilter)) return false;
        if (isDocument(item)) return true;
        return !isAlwaysOn(item);
      }),
    [allItems, scopeFilter],
  );

  const memoryCountAll = allItems.filter(
    item => !isDocument(item) && isAlwaysOn(item),
  ).length;
  const sourceCountAll = allItems.filter(
    item => isDocument(item) || !isAlwaysOn(item),
  ).length;

  const scopeCounts = useMemo(() => {
    const team = allItems.filter(i => i.scope === 'tenant').length;
    const mine = allItems.filter(i => i.scope === 'user').length;
    return { all: allItems.length, team, mine };
  }, [allItems]);

  const openItem = (item: ContextItem, readOnly?: boolean) => {
    if (isDocument(item)) {
      onOpenSource(item.context_id, item.scope, readOnly);
    } else {
      onOpenNote(item.context_id, item.scope, readOnly);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#3C3C3C] border-t-transparent" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <AlertCircle className="h-8 w-8 text-red-500 mb-2" />
        <p className="text-sm text-[#252F33]">{loadError}</p>
        <button
          type="button"
          onClick={refresh}
          className="mt-3 text-sm text-[#006EAF] hover:underline">
          Try again
        </button>
      </div>
    );
  }

  const tally =
    memoryCountAll === 0 && sourceCountAll === 0
      ? 'No memory · no sources'
      : `${memoryCountAll} memor${
          memoryCountAll === 1 ? 'y' : 'ies'
        } · ${sourceCountAll} source${sourceCountAll === 1 ? '' : 's'}`;

  const hasAnySources = allItems.some(
    item => isDocument(item) || (!isDocument(item) && !isAlwaysOn(item)),
  );

  return (
    <div className="max-w-[820px] mx-auto pb-8">
      <div className="rounded-xl border border-[#E4E9EC] bg-white px-5 py-5 sm:px-6 sm:py-6 shadow-[0_1px_2px_rgba(16,24,40,.04)] mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-[1.65rem] font-semibold text-[#252F33] tracking-tight leading-tight">
              Knowledge
            </h1>
            <p className="text-[13.5px] text-[#4A5A61] mt-2 leading-relaxed max-w-lg">
              Give Bernoulli the utility-specific context it cannot learn from
              asset data alone.{' '}
              {onLearnMore && (
                <button
                  type="button"
                  onClick={onLearnMore}
                  className="text-[#007CA0] hover:underline font-medium">
                  Learn more
                </button>
              )}
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              <StatChip label="Memory" value={memoryCountAll} />
              <StatChip label="Sources" value={sourceCountAll} />
              {memoryCountAll === 0 && sourceCountAll === 0 ? (
                <span className="text-xs text-[#768D95] self-center">
                  {tally}
                </span>
              ) : null}
            </div>
          </div>
          <KnowledgeAddMenu
            isAdmin={isAdmin}
            onAddSource={onUploadSource}
            onAddNote={scope => onCreateNew(scope)}
          />
        </div>
      </div>

      <KnowledgeScopePills
        value={scopeFilter}
        onChange={setScopeFilter}
        options={[
          { id: 'all', label: 'Everything', count: scopeCounts.all },
          {
            id: 'team',
            label: 'Shared with the team',
            count: scopeCounts.team,
          },
          { id: 'mine', label: 'Only mine', count: scopeCounts.mine },
        ]}
      />

      <section className="mt-7">
        <SectionHeading
          title="Memory"
          accent="bg-[#007CA0]"
        />
        <p className="text-[12.5px] text-[#768D95] mt-1 mb-3.5 leading-relaxed max-w-xl pl-3.5">
          A short summary Bernoulli keeps in mind across conversations. Notes
          set to &ldquo;Always considered&rdquo; appear here and are injected
          when relevant.
        </p>

        {memoryItems.length > 0 ? (
          <div className="space-y-2">
            {memoryItems.map(item => (
              <KnowledgeMemoryPanel
                key={item.context_id}
                item={item}
                onClick={() =>
                  openItem(
                    item,
                    item.scope === 'tenant' &&
                      'is_own_tenant' in item &&
                      !item.is_own_tenant,
                  )
                }
              />
            ))}
          </div>
        ) : scopeFilter === 'team' ? (
          <EmptyState
            title="Nothing shared with the team yet"
            body="Sources and memory shared here are available to everyone in your team."
          />
        ) : scopeFilter === 'mine' ? (
          <EmptyState
            title="Nothing saved just for you"
            body="Private sources and memory appear here. Your team cannot see them unless you share them."
          />
        ) : hasAnySources ? (
          <EmptyState
            title="Your sources are ready"
            body="Bernoulli can already answer from these files and show its evidence. Add a note set to Always considered for context that should shape every relevant answer."
            action={
              <button
                type="button"
                onClick={() =>
                  onCreateNew('user', { defaultApplyMode: 'always' })
                }
                className="inline-flex items-center gap-1.5 rounded-md bg-[#252F33] text-white text-sm font-medium px-3.5 py-2 hover:bg-[#1a2226]">
                <StickyNote className="h-4 w-4" />
                Add always-on memory
              </button>
            }
            footnote="Keeping sources only is a perfectly good place to stop."
          />
        ) : (
          <EmptyState
            title="Give Bernoulli context beyond asset data"
            body="Add plans, standards, commitments or operating procedures. Bernoulli will use the right source when it needs evidence, and help you capture the few things it should always keep in mind."
            action={
              <button
                type="button"
                onClick={() => onUploadSource('user')}
                className="inline-flex items-center gap-1.5 rounded-md bg-[#252F33] text-white text-sm font-medium px-3.5 py-2 hover:bg-[#1a2226]">
                Add sources
              </button>
            }
            footnote="Good first sources: a level-of-service plan, a rehabilitation standard, a consent decree, or an operating procedure."
          />
        )}
      </section>

      <section className="mt-10">
        <SectionHeading
          title="Sources"
          accent="bg-[#5C7A66]"
        />
        <p className="text-[12.5px] text-[#768D95] mt-1 mb-3.5 leading-relaxed max-w-xl pl-3.5">
          Documents and notes Bernoulli can use for detail and evidence when you
          add them to a conversation.
        </p>

        {sourceItems.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {sourceItems.map(item => {
              const readOnly =
                item.scope === 'tenant' &&
                'is_own_tenant' in item &&
                !item.is_own_tenant;
              const linkedMemory = memoryItems
                .filter(m => m.name !== item.name)
                .slice(0, 2)
                .map(m => m.name);
              return (
                <KnowledgeSourceTile
                  key={item.context_id}
                  item={item}
                  memoryNames={linkedMemory}
                  readOnly={readOnly}
                  onClick={() => openItem(item, readOnly)}
                />
              );
            })}
          </div>
        ) : (
          <EmptyState
            compact
            body="No sources in this view."
            action={
              scopeFilter !== 'team' || isAdmin ? (
                <button
                  type="button"
                  onClick={() =>
                    onUploadSource(scopeFilter === 'team' ? 'tenant' : 'user')
                  }
                  className="text-sm text-[#007CA0] hover:underline font-medium">
                  Add a source
                </button>
              ) : undefined
            }
          />
        )}
      </section>

      <div className="flex items-start gap-2.5 text-[11.5px] text-[#768D95] mt-8 leading-relaxed rounded-lg bg-white/70 border border-[#E8EEF1] px-4 py-3">
        <HelpCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-[#007CA0]" />
        <p>
          Memory is in there whether you pick it or not — it is the always-on
          context Bernoulli reads when your question needs it. Sources are the
          evidence you attach in chat.
        </p>
      </div>
    </div>
  );
};

const StatChip = ({ label, value }: { label: string; value: number }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-[#E4E9EC] bg-[#FAFBFC] px-2.5 py-1 text-xs text-[#41525A]">
    <span className="font-semibold text-[#252F33] tabular-nums">{value}</span>
    {label}
  </span>
);

const SectionHeading = ({
  title,
  accent,
}: {
  title: string;
  accent: string;
}) => (
  <div className="flex items-center gap-2.5">
    <span className={`h-5 w-1 rounded-full ${accent}`} aria-hidden />
    <h2 className="text-[15px] font-semibold text-[#252F33] tracking-tight">
      {title}
    </h2>
  </div>
);

const EmptyState = ({
  title,
  body,
  action,
  footnote,
  compact,
}: {
  title?: string;
  body: string;
  action?: React.ReactNode;
  footnote?: string;
  compact?: boolean;
}) => (
  <div
    className={`rounded-lg border border-dashed border-[#C8D4DA] bg-gradient-to-b from-white to-[#FAFCFD] text-center ${
      compact ? 'py-6 px-5' : 'py-8 px-6'
    }`}>
    {title && <p className="text-sm font-semibold text-[#252F33]">{title}</p>}
    <p
      className={`text-[12.5px] text-[#768D95] leading-relaxed max-w-sm mx-auto ${
        title ? 'mt-1.5' : ''
      }`}>
      {body}
    </p>
    {action && <div className="mt-4">{action}</div>}
    {footnote && (
      <p className="text-[11.5px] text-[#768D95] mt-4">{footnote}</p>
    )}
  </div>
);
